import { formatClinic } from "utils/clinics.util";

import { formatValidQueryParamUrlCommaSeparated } from "../utils/common";
import { createPaginatedEndpoint } from "../utils/services.util";
import { API_TAGS, emptySplitApi } from "./empty-split-api";
import {
  Clinic,
  ClinicFilters,
  ClinicRequest,
  ClinicResponse,
  ClinicsResponse
} from "./types/clinics.type";

// Lightweight query for initial clinic loading (clinic selector)
export const CLINICS_MINIMAL_QUERY =
  "{id,name,display_name,timezone,practice{id,name},region{id,name},type,is_active}";

// Full query for complete clinic details (loaded on selection)
export const CLINICS_QUERY =
  "{id,name,display_name,timezone,is_claim_md_account_key_set,practice{id,name,timezone,ein,npi,non_npi_id,non_npi_id_qualifier,is_claim_md_account_key_set,is_messaging_enabled,is_emailing_enabled,default_appointment_reminder_duration,tilled_account_id,justifi_account_id,logo,card_processing_provider,has_quickbooks_token,is_quickbooks_enabled,scheduling_allow_overlap,scheduling_should_create_schedule_for_staff,scheduling_should_notify,products_use_supplier_price,scheduling_staff_availability,product_requests_deactivate_existing,private_feedback_request_break,google_review_feedback_request_break,appointment_information_disabled,no_reply_email,country,private_feedback_request_disabled,is_hipaa_compliance_for_quickbooks_enabled,reschedule_disabled,patient_settings,billing_name,billing_street_address_1,billing_street_address_2,billing_city,billing_state,billing_country,billing_zip_code,billing_phone,direct_mail_enabled,sale_lock_days,unit_price_lock,product_lock,collect_outcome,no_show_disabled,limit_to_current_clinic,private_feedback_textual_request_disabled,appointment_cancelled_message_disabled,suno_comms_id,has_multiple_companies,message_auto_read_enabled,save_payment_card,products_use_barcode_scanner,go_live_at,billing_use_practice_insurers_only,create_all_sales_in_draft_status,billing_available_payment_methods,billing_claim_era_sync,use_room_traffic,quickbooks_refunds_account,allowed_ips,auto_create_patient_in_noah,qbo_sync_refunds,qbo_taxes_line_item_enabled_on,use_unit_price_for_claims,allow_tilled_refund,enforce_delivery_before_ready_to_bill},ein,npi,non_npi_id,non_npi_id_qualifier,street_address_1,street_address_2,city,state,country,zip_code,phone,fax_phone,is_messaging_enabled,is_emailing_enabled,default_appointment_reminder_duration,tilled_account_id,google_place_id,review_link,justifi_account_id,card_processing_provider,noah_provider,noah_alias,noah_tenant_id,scheduling_staff_availability,web_scheduling_staff_selection,no_reply_email,type,external_id,dba,opened_date,closed_date,region{id,created_at,name,is_active,noah_location_id},is_active,sms_phone,quickbooks_token{id,name,description,quickbooks_company{id,realm_id,company_name},quickbooks_product_services_settings},logo,extra{id,billng_street_address_1,billng_street_address_2,billng_city,billng_state,billng_country,billng_zip,billng_phone},quickbooks_realm_id,suno_comms_id,quickbooks_company{id,realm_id,company_name},noah_auto_sync,web_scheduler_email_recipient,box_folder_id,place_of_service,default_assignee,caption_call_referral_code,five_keys_url,business_name,neon_origin_url,neon_vendor_app_id,neon_crm_app_id}";

export const clinicsApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getClinicsPaginated: createPaginatedEndpoint<
      ClinicFilters,
      ClinicResponse[]
    >(builder, {
      queryFn: (body) => ({
        url: "/clinics/",
        params: { ...body, ordering: "name", query: CLINICS_QUERY }
      }),
      providesTags: ["Clinics"],
      transformResponseFn: (response: ClinicResponse[]) =>
        response.map(formatClinic)
    }),
    getClinics: builder.query<ClinicsResponse, ClinicFilters>({
      query: ({ query = CLINICS_QUERY, ...filters }) =>
        `/clinics/?ordering=name&query=${query}&${formatValidQueryParamUrlCommaSeparated(
          filters
        )}`,
      providesTags: ["Clinics"],
      transformResponse: (response: ClinicResponse[]) =>
        response.map(formatClinic)
    }),
    getClinic: builder.query<Clinic, { clinicId: number; query?: string }>({
      query: ({ clinicId, query = CLINICS_QUERY }) =>
        `/clinics/${clinicId}/?query=${query}`,
      providesTags: ["Clinics"],
      transformResponse: (response: ClinicResponse) => formatClinic(response)
    }),
    createClinic: builder.mutation<Clinic, ClinicRequest>({
      query: (body) => ({
        url: "/clinics/",
        method: "POST",
        body: { ...body, quickbooks_token: body.quickbooks_token?.id }
      }),
      invalidatesTags: ["Clinics"]
    }),
    updateClinic: builder.mutation<
      Clinic,
      Partial<ClinicRequest> & { id: number }
    >({
      query: ({ id, ...body }) => ({
        url: `/clinics/${id}/`,
        method: "PATCH",
        body: { ...body, quickbooks_token: body.quickbooks_token?.id }
      }),
      invalidatesTags: (result, _, { users, products }) => {
        const tags: (typeof API_TAGS)[number][] = ["Clinics"];

        if (users) {
          tags.push("Staff");
          tags.push("StaffLicenseDefaults");
        }

        if (products) {
          tags.push("Products");
        }

        return tags;
      }
    }),
    getUnreadFaxCount: builder.query<{ count: number }, { clinicId: number }>({
      query: ({ clinicId }) => `/clinics/${clinicId}/faxes/unread-fax-count/`,
      providesTags: ["Faxes", "FaxesCount"]
    }),
    readAllFaxes: builder.mutation<
      { updated_count: number },
      { clinicId: number }
    >({
      query: ({ clinicId }) => ({
        url: `/clinics/${clinicId}/faxes/read-all/`,
        method: "POST"
      }),
      invalidatesTags: ["FaxesCount"]
    })
  })
});

export const {
  useGetClinicsQuery,
  useLazyGetClinicsQuery,
  useGetClinicQuery,
  useLazyGetClinicQuery,
  useCreateClinicMutation,
  useUpdateClinicMutation,
  useGetUnreadFaxCountQuery,
  useReadAllFaxesMutation
} = clinicsApi;
